import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  ListGroup,
  Image,
  Badge,
  Form,
  Alert,
  Spinner,
  Tabs,
  Tab,
  Table
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getCastaways } from '../api/castaways';
import { Castaway, League } from '../types';
import TribeBadge, { getTribeColor } from './TribeBadge';
import CastawayBioModal from './CastawayBioModal';
import './LeagueStandings.css';
import '../App.css';

const getPreviewUrl = (url: string) => {
  if (!url) return '';
  if (url.startsWith('https://static.wikia.nocookie.net/')) {
    return `${process.env.REACT_APP_API_URL}/api/admin/image-proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
};

const renderAvatar = (castaway: Castaway, size = 48, borderColor?: string, extraStyle: React.CSSProperties = {}) => {
  const url = getPreviewUrl(castaway.imageUrl);
  const border = borderColor ? `3px solid ${borderColor}` : undefined;
  if (!url) {
    return (
      <div
        className="rounded-circle d-flex align-items-center justify-content-center bg-secondary text-white fw-bold me-2"
        style={{
          width: `${size}px`,
          height: `${size}px`,
          minWidth: `${size}px`,
          fontSize: `${Math.round(size * 0.4)}px`,
          border,
          boxSizing: 'border-box',
          ...extraStyle
        }}
        title={castaway.name}
      >
        {castaway.name.charAt(0)}
      </div>
    );
  }
  return (
    <Image
      src={url}
      referrerPolicy="no-referrer"
      roundedCircle
      onError={(e: any) => {
        const target = e.target as HTMLElement;
        target.style.display = 'none';
        if (target.parentElement && !target.parentElement.querySelector('.fallback-initials')) {
          const fallback = document.createElement('div');
          fallback.className = 'rounded-circle d-flex align-items-center justify-content-center bg-secondary text-white fw-bold fallback-initials me-2';
          fallback.style.width = `${size}px`;
          fallback.style.height = `${size}px`;
          fallback.style.fontSize = `${Math.round(size * 0.4)}px`;
          fallback.innerText = castaway.name.charAt(0);
          if (border) fallback.style.border = border;
          target.parentElement.appendChild(fallback);
        }
      }}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        minWidth: `${size}px`,
        objectFit: 'cover',
        border,
        ...extraStyle
      }}
      title={castaway.name}
    />
  );
};

interface AdminMockDraftProps {
  userMode?: boolean;
  defaultSeason?: number;
}

const AdminMockDraft: React.FC<AdminMockDraftProps> = ({ userMode = false, defaultSeason }) => {
  const [adminSeason, setAdminSeason] = useState<number>(() => {
    const query = new URLSearchParams(window.location.search);
    const qSeason = Number(query.get('season'));
    if (Number.isInteger(qSeason) && qSeason > 0) return qSeason;
    if (defaultSeason) return defaultSeason;
    if (userMode) {
      // In practice mode, default to Season 51 (the new format being practiced)
      return 51;
    }
    return parseInt(localStorage.getItem('adminSelectedSeason') || '51');
  });
  const [availableSeasons, setAvailableSeasons] = useState<number[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [allCastaways, setAllCastaways] = useState<Castaway[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Draft simulator state
  const [rosterSize, setRosterSize] = useState<number>(5);
  const activeLimit = 3;
  const [mockTeam, setMockTeam] = useState<Castaway[]>([]);
  const [activeCastawayIds, setActiveCastawayIds] = useState<number[]>([]);
  const [eliminatedIds, setEliminatedIds] = useState<Set<number>>(new Set());
  const [draftLocked, setDraftLocked] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('draft');
  const [selectedBioCastaway, setSelectedBioCastaway] = useState<Castaway | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTribe, setSelectedTribe] = useState('ALL');

  const fetchLeagues = useCallback(async (season: number, preferredLeagueId?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`);
      if (!response.ok) throw new Error('Failed to fetch leagues');
      const data = await response.json();
      const seasonLeagues = (data.leagues || []).filter((l: any) => (l.season || 49) === season);
      setLeagues(seasonLeagues);
      if (seasonLeagues.length > 0) {
        const query = new URLSearchParams(window.location.search);
        const qLeagueId = preferredLeagueId || query.get('leagueId');
        const targetLeague = (qLeagueId && seasonLeagues.find((l: any) => l.id === qLeagueId)) || seasonLeagues[0];
        setSelectedLeagueId(targetLeague.id);
        setRosterSize(targetLeague.rosterSize || (season >= 51 ? 5 : 3));
      } else {
        setSelectedLeagueId('');
        setAllCastaways([]);
        setMockTeam([]);
      }
    } catch (err: any) {
      setError(err.message || 'Could not load leagues.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/seasons`)
      .then(r => r.ok ? r.json() : { seasons: [51, 50, 49] })
      .then(d => {
        const list = (d.seasons || [51, 50, 49]).sort((a: number, b: number) => b - a);
        setAvailableSeasons(list);
      })
      .catch(() => setAvailableSeasons([51, 50, 49]));
  }, []);

  useEffect(() => {
    fetchLeagues(adminSeason);

    const handleSeasonChange = () => {
      // Don't auto-reset if URL explicitly specified the season
      const query = new URLSearchParams(window.location.search);
      const qSeason = Number(query.get('season'));
      if (Number.isInteger(qSeason) && qSeason > 0) return;

      const newSeason = userMode
        ? 51
        : parseInt(localStorage.getItem('adminSelectedSeason') || '51');
      setAdminSeason(newSeason);
      fetchLeagues(newSeason);
      setMockTeam([]);
      setActiveCastawayIds([]);
      setEliminatedIds(new Set());
      setDraftLocked(false);
    };

    const eventName = userMode ? 'seasonChanged' : 'adminSeasonChanged';
    window.addEventListener(eventName, handleSeasonChange);
    return () => window.removeEventListener(eventName, handleSeasonChange);
  }, [adminSeason, fetchLeagues, userMode]);

  useEffect(() => {
    if (!selectedLeagueId) return;
    const loadCastaways = async () => {
      setIsLoading(true);
      try {
        const list = await getCastaways(selectedLeagueId);
        setAllCastaways(list);
      } catch (err: any) {
        setError(err.message || 'Could not load castaways.');
      } finally {
        setIsLoading(false);
      }
    };
    loadCastaways();
  }, [selectedLeagueId]);

  // Unique tribes for filter
  const tribes = useMemo(() => {
    const set = new Set<string>();
    allCastaways.forEach(c => {
      if (c.tribe) set.add(c.tribe);
    });
    return Array.from(set);
  }, [allCastaways]);

  // Available castaways (not in mockTeam)
  const availableCastaways = useMemo(() => {
    const teamIds = new Set(mockTeam.map(c => c.id));
    return allCastaways.filter(c => {
      if (teamIds.has(c.id)) return false;
      if (selectedTribe !== 'ALL' && c.tribe !== selectedTribe) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return c.name.toLowerCase().includes(q) || c.occupation.toLowerCase().includes(q);
      }
      return true;
    });
  }, [allCastaways, mockTeam, selectedTribe, searchQuery]);

  // Draft handler
  const draftCastaway = (castaway: Castaway) => {
    if (draftLocked) return;
    if (mockTeam.length >= rosterSize) {
      alert(`Your roster is full! Maximum ${rosterSize} castaways allowed.`);
      return;
    }
    const updated = [...mockTeam, castaway];
    setMockTeam(updated);
    // Auto-activate if under activeLimit
    if (activeCastawayIds.length < activeLimit) {
      setActiveCastawayIds(prev => [...prev, castaway.id]);
    }
  };

  // Remove handler
  const removeCastaway = (castawayId: number) => {
    if (draftLocked) return;
    setMockTeam(prev => prev.filter(c => c.id !== castawayId));
    setActiveCastawayIds(prev => prev.filter(id => id !== castawayId));
    setEliminatedIds(prev => {
      const next = new Set(prev);
      next.delete(castawayId);
      return next;
    });
  };

  // Quick random 5 draft
  const randomDraft = () => {
    if (allCastaways.length === 0) return;
    const shuffled = [...allCastaways].sort(() => 0.5 - Math.random());
    const picked = shuffled.slice(0, rosterSize);
    setMockTeam(picked);
    setActiveCastawayIds(picked.slice(0, activeLimit).map(c => c.id));
    setEliminatedIds(new Set());
    setDraftLocked(false);
  };

  // Reset mock draft
  const resetDraft = () => {
    setMockTeam([]);
    setActiveCastawayIds([]);
    setEliminatedIds(new Set());
    setDraftLocked(false);
  };

  // Toggle active/bench
  const toggleActive = (castawayId: number) => {
    if (eliminatedIds.has(castawayId)) {
      alert('This player is eliminated and cannot be in the active scoring lineup!');
      return;
    }
    if (activeCastawayIds.includes(castawayId)) {
      setActiveCastawayIds(prev => prev.filter(id => id !== castawayId));
    } else {
      if (activeCastawayIds.length >= activeLimit) {
        alert(`You can only have ${activeLimit} active players scoring each week! Swap someone to the bench first.`);
        return;
      }
      setActiveCastawayIds(prev => [...prev, castawayId]);
    }
  };

  // Toggle simulated elimination (voted out)
  const toggleElimination = (castawayId: number) => {
    setEliminatedIds(prev => {
      const next = new Set(prev);
      if (next.has(castawayId)) {
        next.delete(castawayId);
      } else {
        next.add(castawayId);
        // If they were active, remove from active
        setActiveCastawayIds(act => act.filter(id => id !== castawayId));
      }
      return next;
    });
  };

  // Simulated scores for mock preview (points per player)
  const mockScores: Record<number, number> = useMemo(() => {
    const scores: Record<number, number> = {};
    mockTeam.forEach((c, idx) => {
      // Generate realistic score points based on active status
      scores[c.id] = (idx + 1) * 7.5;
    });
    return scores;
  }, [mockTeam]);

  const activePlayers = useMemo(() => {
    return mockTeam.filter(c => activeCastawayIds.includes(c.id));
  }, [mockTeam, activeCastawayIds]);

  const benchPlayers = useMemo(() => {
    return mockTeam.filter(c => !activeCastawayIds.includes(c.id));
  }, [mockTeam, activeCastawayIds]);

  const totalScore = useMemo(() => {
    // Only active players score!
    return activePlayers.reduce((acc, c) => acc + (mockScores[c.id] || 0), 0) + 6; // +6 mock elim pick points
  }, [activePlayers, mockScores]);

  const currentLeague = leagues.find(l => l.id === selectedLeagueId);

  return (
    <Container className="mt-4 mb-5">
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <Link to={userMode ? "/" : "/admin/dashboard"} className="text-decoration-none">
          &larr; Back to {userMode ? "Home" : "Admin Dashboard"}
        </Link>
        <Badge bg={userMode ? "primary" : "secondary"} className="px-3 py-2">
          {userMode ? "Practice Sandbox" : "Admin Mock Sandbox"}
        </Badge>
      </div>

      <Card className="shadow-sm home-card mb-4 border-0">
        <Card.Header as="h4" className="card-title-custom text-center bg-dark text-white py-3">
          {userMode ? "Practice Mock Draft" : "Mock Draft & Roster Sandbox"} (Season {adminSeason})
        </Card.Header>
        <Card.Body>
          <p className="text-muted text-center mb-4">
            {userMode
              ? "Practice drafting your 5-player roster, test weekly active/bench lineups, and see what your team will look like on your dashboard before the draft opens!"
              : "Test and preview the user experience of drafting, weekly active/bench swaps, and home dashboard card visuals without affecting real league data or user accounts."
            }
          </p>

          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

          {/* Season Switcher Pills */}
          {availableSeasons.length > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-2 mb-4 flex-wrap">
              <span className="text-muted fw-bold me-1">Practice Season:</span>
              {availableSeasons.map(s => (
                <Button
                  key={s}
                  size="sm"
                  variant={adminSeason === s ? 'primary' : 'outline-secondary'}
                  onClick={() => {
                    setAdminSeason(s);
                    fetchLeagues(s);
                    setMockTeam([]);
                    setActiveCastawayIds([]);
                    setEliminatedIds(new Set());
                    setDraftLocked(false);
                  }}
                >
                  Season {s} {s >= 51 ? '(New 5-Player Format)' : '(Legacy 3-Player)'}
                </Button>
              ))}
            </div>
          )}

          {/* Configuration controls */}
          <Row className="g-3 align-items-end mb-4 p-3 bg-light rounded border">
            <Col md={4}>
              <Form.Label className="fw-bold">Select League:</Form.Label>
              <Form.Select
                value={selectedLeagueId}
                onChange={e => {
                  setSelectedLeagueId(e.target.value);
                  const l = leagues.find(lg => lg.id === e.target.value);
                  if (l) setRosterSize(l.rosterSize || (adminSeason >= 51 ? 5 : 3));
                }}
                disabled={isLoading || leagues.length === 0}
              >
                {leagues.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.name} (Season {l.season})
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col md={3}>
              <Form.Label className="fw-bold">Roster Size Format:</Form.Label>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  variant={rosterSize === 5 ? 'primary' : 'outline-primary'}
                  onClick={() => setRosterSize(5)}
                >
                  5 Players (New S51)
                </Button>
                <Button
                  size="sm"
                  variant={rosterSize === 3 ? 'secondary' : 'outline-secondary'}
                  onClick={() => setRosterSize(3)}
                >
                  3 Players (Legacy)
                </Button>
              </div>
            </Col>

            <Col md={5} className="d-flex justify-content-md-end gap-2 flex-wrap">
              <Button variant="outline-success" size="sm" onClick={randomDraft} disabled={allCastaways.length === 0}>
                Quick Random {rosterSize}
              </Button>
              <Button
                variant={draftLocked ? 'warning' : 'outline-warning'}
                size="sm"
                onClick={() => setDraftLocked(!draftLocked)}
                disabled={mockTeam.length === 0}
              >
                {draftLocked ? 'Unlock Draft' : 'Lock In Draft'}
              </Button>
              <Button variant="outline-danger" size="sm" onClick={resetDraft}>
                Reset
              </Button>
            </Col>
          </Row>

          {isLoading && (
            <div className="text-center p-5">
              <Spinner animation="border" />
            </div>
          )}

          {!isLoading && allCastaways.length === 0 && (
            <Alert variant="warning">
              No castaways found for this league. Set up castaways in League Management first!
            </Alert>
          )}

          {!isLoading && allCastaways.length > 0 && (
            <Tabs
              activeKey={activeTab}
              onSelect={k => setActiveTab(k || 'draft')}
              className="mb-4"
              id="mock-draft-tabs"
            >
              {/* TAB 1: DRAFTING EXPERIENCE */}
              <Tab eventKey="draft" title={`1. Draft Experience (${mockTeam.length}/${rosterSize} Selected)`}>
                <Alert variant={mockTeam.length === rosterSize ? 'success' : 'info'} className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>User Draft Experience:</strong> Players select {rosterSize} castaways for their roster.
                    {mockTeam.length === rosterSize && ' Roster is full! They can now lock it in.'}
                  </div>
                  <Badge bg={draftLocked ? 'danger' : 'primary'} className="p-2">
                    {draftLocked ? 'Draft Status: LOCKED' : 'Draft Status: OPEN'}
                  </Badge>
                </Alert>

                <Row>
                  {/* Left Column: Available Castaways */}
                  <Col md={7}>
                    <Card className="mb-4 home-card shadow-sm">
                      <Card.Header as="h5" className="d-flex justify-content-between align-items-center">
                        <span>Available Castaways ({availableCastaways.length})</span>
                      </Card.Header>
                      <Card.Body className="p-2">
                        {/* Filters */}
                        <Row className="g-2 mb-2">
                          <Col md={8}>
                            <Form.Control
                              size="sm"
                              placeholder="Search castaway name or occupation..."
                              value={searchQuery}
                              onChange={e => setSearchQuery(e.target.value)}
                            />
                          </Col>
                          <Col md={4}>
                            <Form.Select
                              size="sm"
                              value={selectedTribe}
                              onChange={e => setSelectedTribe(e.target.value)}
                            >
                              <option value="ALL">All Tribes</option>
                              {tribes.map(t => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </Form.Select>
                          </Col>
                        </Row>

                        <ListGroup variant="flush" style={{ maxHeight: '550px', overflowY: 'auto' }}>
                          {availableCastaways.map(castaway => (
                            <ListGroup.Item
                              key={castaway.id}
                              className="d-flex justify-content-between align-items-center p-2"
                            >
                              <div 
                                className="d-flex align-items-center cursor-pointer"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setSelectedBioCastaway(castaway)}
                                title="Click to view full contestant bio & quotes"
                              >
                                <div style={{ marginRight: '12px' }}>
                                  {renderAvatar(castaway, 48, getTribeColor(castaway.tribe, currentLeague?.tribes, castaway.tribeColor))}
                                </div>
                                <div>
                                  <strong className="text-primary">{castaway.name}</strong> ({castaway.age}) <span className="text-muted small">ℹ️</span>
                                  <div className="d-flex align-items-center gap-1 mt-1">
                                    <TribeBadge tribeName={castaway.tribe} tribes={currentLeague?.tribes} tribeColor={castaway.tribeColor} />
                                    <small className="text-muted">{castaway.occupation}</small>
                                  </div>
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-1">
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  onClick={() => setSelectedBioCastaway(castaway)}
                                  title="View Bio"
                                >
                                  Bio
                                </Button>
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={() => draftCastaway(castaway)}
                                  disabled={mockTeam.length >= rosterSize || draftLocked}
                                >
                                  Draft
                                </Button>
                              </div>
                            </ListGroup.Item>
                          ))}
                          {availableCastaways.length === 0 && (
                            <ListGroup.Item className="text-center text-muted p-4">
                              No matching castaways available.
                            </ListGroup.Item>
                          )}
                        </ListGroup>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* Right Column: User's Drafted Team */}
                  <Col md={5}>
                    <Card className="home-card shadow-sm mb-3">
                      <Card.Header as="h5" className="d-flex justify-content-between align-items-center bg-primary text-white">
                        <span>My Roster ({mockTeam.length}/{rosterSize})</span>
                        <Badge bg="light" text="dark">{rosterSize - mockTeam.length} picks left</Badge>
                      </Card.Header>
                      <ListGroup variant="flush">
                        {mockTeam.map(castaway => (
                          <ListGroup.Item key={castaway.id} className="d-flex justify-content-between align-items-center p-2">
                            <div 
                              className="d-flex align-items-center cursor-pointer"
                              style={{ cursor: 'pointer' }}
                              onClick={() => setSelectedBioCastaway(castaway)}
                              title="Click to view full contestant bio & quotes"
                            >
                              <div style={{ marginRight: '10px' }}>
                                {renderAvatar(castaway, 40, getTribeColor(castaway.tribe, currentLeague?.tribes, castaway.tribeColor))}
                              </div>
                              <div>
                                <strong className="text-primary">{castaway.name}</strong>
                                <div className="d-flex align-items-center gap-1 mt-1">
                                  <TribeBadge tribeName={castaway.tribe} tribes={currentLeague?.tribes} tribeColor={castaway.tribeColor} style={{ fontSize: '0.65rem' }} />
                                  {activeCastawayIds.includes(castaway.id) && (
                                    <Badge bg="success" style={{ fontSize: '0.65rem' }}>Active 3</Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-1">
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => setSelectedBioCastaway(castaway)}
                                title="View Bio"
                              >
                                Bio
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeCastaway(castaway.id)}
                                disabled={draftLocked}
                              >
                                Remove
                              </Button>
                            </div>
                          </ListGroup.Item>
                        ))}
                        {mockTeam.length === 0 && (
                          <ListGroup.Item className="text-center text-muted p-4">
                            No castaways drafted yet. Click "Draft" on any castaway to build your team.
                          </ListGroup.Item>
                        )}
                      </ListGroup>
                    </Card>

                    <div className="d-grid gap-2">
                      <Button
                        variant={draftLocked ? 'secondary' : 'primary'}
                        size="lg"
                        onClick={() => setDraftLocked(!draftLocked)}
                        disabled={mockTeam.length !== rosterSize}
                      >
                        {draftLocked ? 'Roster Locked (Click to Unlock)' : 'Lock In Roster'}
                      </Button>
                      {mockTeam.length === rosterSize && (
                        <Button variant="outline-success" onClick={() => setActiveTab('lineup')}>
                          Next: Test Weekly Active/Bench Lineup &rarr;
                        </Button>
                      )}
                    </div>
                  </Col>
                </Row>
              </Tab>

              {/* TAB 2: WEEKLY ACTIVE / BENCH LINEUP */}
              <Tab eventKey="lineup" title={`2. Weekly Lineup (3 Active / ${Math.max(0, mockTeam.length - 3)} Bench)`}>
                <Alert variant="info">
                  <strong>Weekly Swap Mechanics:</strong> In Season 51, players maintain <strong>3 Active</strong> (scoring) players and bench the remaining. Each week before the 7:00 PM ET deadline, they can freely swap bench players into active. If an active player is voted out, they can swap in a bench player for future episodes.
                </Alert>

                <Row className="g-4">
                  {/* Active Lineup (Scoring) */}
                  <Col md={6}>
                    <Card className="shadow-sm border-success h-100">
                      <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
                        <span className="fw-bold">Active Lineup ({activePlayers.length}/{activeLimit})</span>
                        <Badge bg="light" text="dark">Points Active</Badge>
                      </Card.Header>
                      <Card.Body>
                        <p className="small text-muted mb-3">
                          These 3 players earn points for the user this episode. Click "Bench Player" to swap them to the bench.
                        </p>
                        <ListGroup variant="flush">
                          {activePlayers.map(castaway => {
                            const isVotedOut = eliminatedIds.has(castaway.id);
                            return (
                              <ListGroup.Item key={castaway.id} className="d-flex justify-content-between align-items-center p-2 mb-2 border rounded">
                                <div className="d-flex align-items-center">
                                  <div className={`position-relative ${isVotedOut ? 'voted-out' : ''}`} style={{ width: '48px', height: '48px', marginRight: '12px' }}>
                                    {renderAvatar(castaway, 48, getTribeColor(castaway.tribe, currentLeague?.tribes, castaway.tribeColor), { width: '100%', height: '100%' })}
                                  </div>
                                  <div>
                                    <strong>{castaway.name}</strong>
                                    <div className="d-flex align-items-center gap-1 mt-1">
                                      <Badge bg="success">Active</Badge>
                                      <TribeBadge tribeName={castaway.tribe} tribes={currentLeague?.tribes} tribeColor={castaway.tribeColor} />
                                    </div>
                                  </div>
                                </div>
                                <div className="d-flex gap-1">
                                  <Button size="sm" variant="outline-warning" onClick={() => toggleActive(castaway.id)}>
                                    Bench
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={isVotedOut ? 'outline-success' : 'outline-danger'}
                                    onClick={() => toggleElimination(castaway.id)}
                                    title="Simulate this player getting voted out"
                                  >
                                    {isVotedOut ? 'Revive' : 'Vote Out'}
                                  </Button>
                                </div>
                              </ListGroup.Item>
                            );
                          })}
                          {activePlayers.length === 0 && (
                            <div className="text-center text-muted p-4 border rounded">
                              No active players assigned! Pick from the bench below.
                            </div>
                          )}
                        </ListGroup>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* Bench Players (Reserves) */}
                  <Col md={6}>
                    <Card className="shadow-sm border-secondary h-100">
                      <Card.Header className="bg-secondary text-white d-flex justify-content-between align-items-center">
                        <span className="fw-bold">Bench / Reserves ({benchPlayers.length})</span>
                        <Badge bg="dark">Not Scoring</Badge>
                      </Card.Header>
                      <Card.Body>
                        <p className="small text-muted mb-3">
                          Bench players do not earn points this episode. If an active player is eliminated or on a tough tribe, swap a benched player into the active 3!
                        </p>
                        <ListGroup variant="flush">
                          {benchPlayers.map(castaway => {
                            const isVotedOut = eliminatedIds.has(castaway.id);
                            return (
                              <ListGroup.Item key={castaway.id} className="d-flex justify-content-between align-items-center p-2 mb-2 border rounded">
                                <div className="d-flex align-items-center">
                                  <div className={`position-relative ${isVotedOut ? 'voted-out' : ''}`} style={{ width: '48px', height: '48px', marginRight: '12px' }}>
                                    {renderAvatar(castaway, 48, getTribeColor(castaway.tribe, currentLeague?.tribes, castaway.tribeColor), { width: '100%', height: '100%' })}
                                  </div>
                                  <div>
                                    <strong className={isVotedOut ? 'text-decoration-line-through text-danger' : ''}>
                                      {castaway.name}
                                    </strong>
                                    <div className="d-flex align-items-center gap-1 mt-1">
                                      {isVotedOut ? (
                                        <Badge bg="danger">Voted Out</Badge>
                                      ) : (
                                        <Badge bg="dark">Bench</Badge>
                                      )}
                                      <TribeBadge tribeName={castaway.tribe} tribes={currentLeague?.tribes} tribeColor={castaway.tribeColor} />
                                    </div>
                                  </div>
                                </div>
                                <div className="d-flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline-success"
                                    onClick={() => toggleActive(castaway.id)}
                                    disabled={isVotedOut || activePlayers.length >= activeLimit}
                                  >
                                    Make Active
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={isVotedOut ? 'outline-success' : 'outline-danger'}
                                    onClick={() => toggleElimination(castaway.id)}
                                    title="Simulate this player getting voted out"
                                  >
                                    {isVotedOut ? 'Revive' : 'Vote Out'}
                                  </Button>
                                </div>
                              </ListGroup.Item>
                            );
                          })}
                          {benchPlayers.length === 0 && (
                            <div className="text-center text-muted p-4 border rounded">
                              No bench players! Draft more players in Tab 1.
                            </div>
                          )}
                        </ListGroup>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab>

              {/* TAB 3: USER DASHBOARD PREVIEW */}
              <Tab eventKey="card-preview" title="3. User Dashboard Preview (Home Page Card)">
                <Alert variant="success">
                  <strong>User View Preview:</strong> Below is how the drafted team displays on the user's Home Dashboard, showing their rank, score, active players with points badges, and elimination states.
                </Alert>

                <Row className="justify-content-center">
                  <Col md={8} lg={6}>
                    <Card className="h-100 shadow home-card border">
                      <Card.Body className="d-flex flex-column p-4">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <Card.Title className="fs-4 text-primary">
                              {currentLeague?.name || `Survivor Season ${adminSeason} Pool`}
                            </Card.Title>
                            <Card.Subtitle className="mb-2 text-muted">
                              <span className="me-2">Score: <strong className="text-success">{totalScore.toFixed(1)}</strong></span>
                              <span className="me-2">Rank: <strong>1</strong></span>
                            </Card.Subtitle>
                            <Card.Subtitle className="mb-2 text-muted">
                              Season {adminSeason}
                            </Card.Subtitle>
                          </div>

                          {/* Team circular avatars with point badges */}
                          <div className="d-flex flex-column align-items-end" style={{ maxWidth: '55%' }}>
                            <div className="d-flex flex-wrap justify-content-end mb-1" style={{ gap: '10px' }}>
                              {mockTeam.map(c => {
                                const isActive = activeCastawayIds.includes(c.id);
                                const isVotedOut = eliminatedIds.has(c.id);
                                const pts = isActive ? (mockScores[c.id] || 0) : 0;

                                return (
                                  <div
                                    key={c.id}
                                    className={`position-relative ${isVotedOut ? 'voted-out' : ''}`}
                                    style={{ width: '46px', height: '46px', marginBottom: '8px' }}
                                    title={`${c.name} (${c.tribe}) - ${isActive ? 'ACTIVE' : 'BENCH'}${isVotedOut ? ' - ELIMINATED' : ''}`}
                                  >
                                    <Image
                                      src={getPreviewUrl(c.imageUrl)}
                                      roundedCircle
                                      thumbnail
                                      referrerPolicy="no-referrer"
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        padding: 0,
                                        objectFit: 'cover',
                                        border: `3px solid ${getTribeColor(c.tribe, currentLeague?.tribes, c.tribeColor)}`,
                                        opacity: isActive ? 1 : 0.6
                                      }}
                                    />
                                    {isActive && (
                                      <Badge
                                        bg="dark"
                                        className="position-absolute"
                                        style={{
                                          bottom: -8,
                                          left: '50%',
                                          transform: 'translateX(-50%)',
                                          fontSize: '0.6rem',
                                          padding: '2px 4px',
                                          border: '1px solid white',
                                          zIndex: 11
                                        }}
                                      >
                                        {pts}
                                      </Badge>
                                    )}
                                    {!isActive && (
                                      <Badge
                                        bg="secondary"
                                        className="position-absolute"
                                        style={{
                                          bottom: -8,
                                          left: '50%',
                                          transform: 'translateX(-50%)',
                                          fontSize: '0.55rem',
                                          padding: '1px 3px',
                                          zIndex: 11
                                        }}
                                      >
                                        BN
                                      </Badge>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#666' }}>
                              Elim Pick Pts: <span className="text-primary">6</span>
                            </div>
                          </div>
                        </div>

                        <Card.Text className="mt-3 mb-2">
                          <strong>Survivor Name:</strong> Mock Tester
                        </Card.Text>

                        <div className="d-grid gap-2 mt-auto">
                          <Button variant="info" size="sm" className="text-white">
                            View Season Highlights
                          </Button>
                          <Button variant="secondary" size="sm">
                            View Current Standings
                          </Button>
                          <Button variant="primary" size="sm">
                            View My Survivor Scores
                          </Button>
                          <Button variant="dark" size="sm">
                            Weekly Elimination Pick
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Standings Table Simulation */}
                <Card className="mt-4 shadow-sm">
                  <Card.Header as="h6">League Standings Table Representation</Card.Header>
                  <Card.Body className="p-0">
                    <Table responsive hover className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Rank</th>
                          <th>Survivor Name</th>
                          <th>Active Lineup (Scoring)</th>
                          <th>Bench (Reserve)</th>
                          <th className="text-end">Total Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>1</strong></td>
                          <td>Mock Tester</td>
                          <td>
                            {activePlayers.map(c => (
                              <span key={c.id} className="me-2 d-inline-flex align-items-center mb-1">
                                <Badge bg="success" className="me-1">{c.name}</Badge>
                                <TribeBadge tribeName={c.tribe} tribes={currentLeague?.tribes} tribeColor={c.tribeColor} style={{ fontSize: '0.65rem' }} />
                              </span>
                            ))}
                            {activePlayers.length === 0 && <span className="text-muted">None</span>}
                          </td>
                          <td>
                            {benchPlayers.map(c => (
                              <span key={c.id} className="me-2 d-inline-flex align-items-center mb-1">
                                <Badge bg={eliminatedIds.has(c.id) ? 'danger' : 'secondary'} className="me-1">
                                  {c.name} {eliminatedIds.has(c.id) ? '(X)' : ''}
                                </Badge>
                                <TribeBadge tribeName={c.tribe} tribes={currentLeague?.tribes} tribeColor={c.tribeColor} style={{ fontSize: '0.65rem' }} />
                              </span>
                            ))}
                            {benchPlayers.length === 0 && <span className="text-muted">None</span>}
                          </td>
                          <td className="text-end fw-bold text-success">
                            {totalScore.toFixed(1)}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          )}
        </Card.Body>
      </Card>

      {/* Castaway Bio Popout Modal */}
      <CastawayBioModal
        show={Boolean(selectedBioCastaway)}
        onHide={() => setSelectedBioCastaway(null)}
        castaway={selectedBioCastaway}
        tribes={currentLeague?.tribes}
        onDraft={(castaway) => draftCastaway(castaway)}
        isDraftable={!draftLocked && mockTeam.length < rosterSize}
        isDrafted={Boolean(selectedBioCastaway && mockTeam.some(c => c.id === selectedBioCastaway.id))}
      />
    </Container>
  );
};

export default AdminMockDraft;
